import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import type { AssetTemplate } from '@/types/assetTemplate';
import { INSTANCE_CONDITION_VALUES } from '../constants/assetConditions';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

interface BulkInstanceCreatorProps {
  templateId: string;
  template: AssetTemplate;
  nextUnitSequence?: number;
  onSuccess: () => void;
  onCancel: () => void;
}

const bulkSchema = z.object({
  quantity: z.string().refine((val) => {
    const num = parseInt(val);
    return num >= 1 && num <= 1000;
  }, 'Quantity must be between 1 and 1000'),
  prefix: z.string().min(1, 'Prefix is required'),
  start_number: z.string().refine((val) => {
    const num = parseInt(val);
    return num >= 1;
  }, 'Start number must be at least 1'),
  padding: z.string().refine((val) => {
    const num = parseInt(val);
    return num >= 1 && num <= 10;
  }, 'Padding must be between 1 and 10'),
  condition: z.enum(INSTANCE_CONDITION_VALUES),
  status: z.enum(['available', 'reserved', 'booked', 'in_transit', 'rented', 'maintenance', 'retired', 'lost', 'damaged']),
  current_location_id: z.string().optional(),
  acquisition_date: z.string().optional(),
  acquisition_cost: z.string().optional(),
});

type BulkFormData = z.infer<typeof bulkSchema>;

const BulkInstanceCreator: React.FC<BulkInstanceCreatorProps> = ({
  templateId,
  template,
  nextUnitSequence = 1,
  onSuccess,
  onCancel,
}) => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [createdCount, setCreatedCount] = useState(0);
  const [locations, setLocations] = useState<any[]>([]);
  const [addLocOpen, setAddLocOpen] = useState(false);
  const [newLoc, setNewLoc] = useState({
    address_line1: '',
    address_line2: '',
    city: '',
    region: '',
    postal_code: '',
    country_code: 'GB',
    address_type: 'warehouse',
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<BulkFormData>({
    resolver: zodResolver(bulkSchema),
    defaultValues: {
      quantity: '10',
      prefix: template.template_code,
      start_number: String(nextUnitSequence),
      padding: '4',
      condition: template.default_condition,
      status: 'available',
      current_location_id: '',
      acquisition_date: '',
      acquisition_cost: '',
    },
  });

  const watchedValues = watch();

  const generatePreview = () => {
    const quantity = parseInt(watchedValues.quantity || '0');
    const startNumber = parseInt(watchedValues.start_number || '1');
    const padding = parseInt(watchedValues.padding || '3');
    const prefix = watchedValues.prefix || '';

    if (quantity > 0 && quantity <= 10) {
      return Array.from({ length: quantity }, (_, i) => {
        const number = (startNumber + i).toString().padStart(padding, '0');
        return prefix ? `${prefix}-${number}` : number;
      });
    }
    return [];
  };

  useEffect(() => {
    setValue('prefix', template.template_code);
    setValue('start_number', String(nextUnitSequence));
  }, [template.template_code, nextUnitSequence, setValue]);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/addresses', { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const json = await res.json();
          setLocations(json.success ? json.data : json);
        }
      } catch (e) {
        console.error('load locations failed', e);
      }
    })();
  }, []);

  const submitForm = async (data: BulkFormData) => {
    try {
      setLoading(true);
      setProgress(0);
      setCreatedCount(0);

      const quantity = parseInt(data.quantity);
      const startNumber = parseInt(data.start_number);
      const padding = parseInt(data.padding);
      const acquisitionCost = data.acquisition_cost ? parseFloat(data.acquisition_cost) : undefined;

      const instances = Array.from({ length: quantity }, (_, i) => {
        const number = (startNumber + i).toString().padStart(padding, '0');
        const instanceNumber = data.prefix ? `${data.prefix}-${number}` : number;

        return {
          template_id: templateId,
          instance_number: instanceNumber,
          current_condition: data.condition,
          current_status: data.status,
          current_location_id: data.current_location_id || undefined,
          acquisition_date: data.acquisition_date || undefined,
          acquisition_cost: acquisitionCost,
        };
      });

      // Create instances in batches of 10 to avoid overwhelming the server
      const batchSize = 10;
      let created = 0;

      for (let i = 0; i < instances.length; i += batchSize) {
        const batch = instances.slice(i, i + batchSize);

        const token = localStorage.getItem('token');
        const response = await fetch('/api/asset-instances/bulk', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ instances: batch }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create instances');
        }

        const result = await response.json();
        created += result.created || batch.length;
        setCreatedCount(created);
        setProgress((created / quantity) * 100);

        // Small delay between batches
        if (i + batchSize < instances.length) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }

      onSuccess();
    } catch (err) {
      console.error('Error creating instances:', err);
      window.alert(err instanceof Error ? err.message : 'Failed to create instances');
    } finally {
      setLoading(false);
    }
  };

  const preview = generatePreview();
  const condition = watch('condition');
  const status = watch('status');
  const locId = watch('current_location_id');

  return (
    <form onSubmit={handleSubmit(submitForm)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Quantity */}
        <div>
          <Label htmlFor="quantity">Quantity *</Label>
          <Input
            id="quantity"
            type="number"
            min="1"
            max="1000"
            {...register('quantity')}
            className={errors.quantity ? 'border-red-500' : ''}
          />
          {errors.quantity && (
            <p className="text-sm text-red-600 mt-1">{errors.quantity.message}</p>
          )}
        </div>

        {/* Product code (read-only) */}
        <div>
          <Label htmlFor="prefix">Product Code *</Label>
          <Input
            id="prefix"
            {...register('prefix')}
            readOnly
            className={`bg-muted ${errors.prefix ? 'border-red-500' : ''}`}
          />
          <p className="text-sm text-gray-500 mt-1">Homebound product code for this template</p>
          {errors.prefix && (
            <p className="text-sm text-red-600 mt-1">{errors.prefix.message}</p>
          )}
        </div>

        {/* Start Number */}
        <div>
          <Label htmlFor="start_number">Start Number *</Label>
          <Input
            id="start_number"
            type="number"
            min="1"
            {...register('start_number')}
            className={errors.start_number ? 'border-red-500' : ''}
          />
          {errors.start_number && (
            <p className="text-sm text-red-600 mt-1">{errors.start_number.message}</p>
          )}
        </div>

        {/* Padding */}
        <div>
          <Label htmlFor="padding">Zero Padding *</Label>
          <Input
            id="padding"
            type="number"
            min="1"
            max="10"
            {...register('padding')}
            className={errors.padding ? 'border-red-500' : ''}
          />
          <p className="text-sm text-gray-500 mt-1">Number of digits (e.g., 3 = 001, 002)</p>
        </div>

        {/* Condition */}
        <div>
          <Label htmlFor="condition">Default Condition *</Label>
          <Select
            value={condition}
            onValueChange={(value) => setValue('condition', value as any)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select condition" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="pre_loved">Pre-Loved</SelectItem>
              <SelectItem value="good">Good</SelectItem>
              <SelectItem value="damaged">Damaged</SelectItem>
              <SelectItem value="needs_repair">Needs Repair</SelectItem>
              <SelectItem value="missing">Missing</SelectItem>
              <SelectItem value="retired">Retired</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Status */}
        <div>
          <Label htmlFor="status">Default Status *</Label>
          <Select
            value={status}
            onValueChange={(value) => setValue('status', value as any)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="available">Available</SelectItem>
              <SelectItem value="rented">Rented</SelectItem>
              <SelectItem value="maintenance">In Maintenance</SelectItem>
              <SelectItem value="lost">Lost</SelectItem>
              <SelectItem value="damaged">Damaged</SelectItem>
              <SelectItem value="retired">Retired</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Location */}
        <div className="md:col-span-2 space-y-2">
          <div className="flex items-center justify-between">
            <Label>Default Location (optional)</Label>
            <Button type="button" variant="outline" size="sm" onClick={() => setAddLocOpen(true)}>Add address</Button>
          </div>
          <Select value={(locId as any) || '__none__'} onValueChange={(v) => setValue('current_location_id', v === '__none__' ? '' : (v as any))}>
            <SelectTrigger><SelectValue placeholder="Select location" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No location</SelectItem>
              {locations.map((loc) => (
                <SelectItem key={loc.id} value={loc.id}>{loc.address_line1}{loc.city ? `, ${loc.city}` : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Acquisition Date */}
        <div>
          <Label htmlFor="acquisition_date">Acquisition Date</Label>
          <Input
            id="acquisition_date"
            type="date"
            {...register('acquisition_date')}
          />
          <p className="text-sm text-gray-500 mt-1">Same for all instances</p>
        </div>

        {/* Acquisition Cost */}
        <div>
          <Label htmlFor="acquisition_cost">Cost Per Unit</Label>
          <Input
            id="acquisition_cost"
            type="number"
            step="0.01"
            min="0"
            placeholder="0.00"
            {...register('acquisition_cost')}
          />
          <p className="text-sm text-gray-500 mt-1">Same for all instances</p>
        </div>
      </div>

      {/* Add location dialog */}
      <Dialog open={addLocOpen} onOpenChange={setAddLocOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add address</DialogTitle>
            <DialogDescription>Create a new address to use as the default location.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <Label>Address Line 1</Label>
              <Input value={newLoc.address_line1} onChange={(e) => setNewLoc({ ...newLoc, address_line1: e.target.value })} />
            </div>
            <div>
              <Label>Address Line 2</Label>
              <Input value={newLoc.address_line2} onChange={(e) => setNewLoc({ ...newLoc, address_line2: e.target.value })} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>City</Label>
                <Input value={newLoc.city} onChange={(e) => setNewLoc({ ...newLoc, city: e.target.value })} />
              </div>
              <div>
                <Label>Region</Label>
                <Input value={newLoc.region} onChange={(e) => setNewLoc({ ...newLoc, region: e.target.value })} />
              </div>
              <div>
                <Label>Postal Code</Label>
                <Input value={newLoc.postal_code} onChange={(e) => setNewLoc({ ...newLoc, postal_code: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Country</Label>
                <Input maxLength={2} value={newLoc.country_code} onChange={(e) => setNewLoc({ ...newLoc, country_code: e.target.value.toUpperCase() })} />
              </div>
              <div className="md:col-span-2">
                <Label>Type</Label>
                <Select value={newLoc.address_type} onValueChange={(v) => setNewLoc({ ...newLoc, address_type: v })}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="warehouse">Warehouse</SelectItem>
                    <SelectItem value="hub">Hub</SelectItem>
                    <SelectItem value="customer_site">Customer Site</SelectItem>
                    <SelectItem value="vendor">Vendor</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setAddLocOpen(false)}>Cancel</Button>
            <Button
              type="button"
              onClick={async () => {
                if (!newLoc.address_line1 || !newLoc.city || !newLoc.country_code) {
                  window.alert('Address line 1, city and country are required');
                  return;
                }
                try {
                  const token = localStorage.getItem('token');
                  const res = await fetch('/api/addresses', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(newLoc),
                  });
                  const data = await res.json();
                  if (!res.ok || !data.success) throw new Error(data.message || 'Failed to create');
                  const created = data.data;
                  setLocations((prev) => [created, ...prev]);
                  setValue('current_location_id', created.id as any);
                  setAddLocOpen(false);
                } catch (err) {
                  console.error('Create location failed', err);
                  window.alert(err instanceof Error ? err.message : 'Failed to create location');
                }
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview */}
      {preview.length > 0 && (
        <div>
          <Label>Preview (first {preview.length} units)</Label>
          <div className="mt-2 p-3 bg-gray-50 rounded-md border">
            <div className="flex flex-wrap gap-2">
              {preview.map((instanceNumber, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-white border rounded text-sm font-mono"
                >
                  {instanceNumber}
                </span>
              ))}
              {parseInt(watchedValues.quantity || '0') > 10 && (
                <span className="px-2 py-1 text-gray-500 text-sm">
                  ... and {parseInt(watchedValues.quantity || '0') - 10} more
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Progress */}
      {loading && (
        <div>
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Creating units...</span>
            <span>{createdCount} / {parseInt(watchedValues.quantity || '0')}</span>
          </div>
          <Progress value={progress} className="w-full" />
        </div>
      )}

      {/* Form Actions */}
      <div className="flex justify-end space-x-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? 'Creating...' : `Create ${watchedValues.quantity || 0} Units`}
        </Button>
      </div>
    </form>
  );
};

export default BulkInstanceCreator;
